import React from "react";
import PropTypes from "prop-types";
import Chart from "react-apexcharts";
import ShimmerLoader from "common/src/components/widget/ShimmerLoader";
import Fragment from "common/src/components/widget/Fragment";
import { useTranslation } from "react-i18next";
import { formatSLONumber } from "../utils/FormatNumbers";

/**
 * Time-series line chart for vehicle overload trends.
 */
const LineChartTimeSeriesWidget = ({
  id,
  title,
  series = [],
  isLoading = false,
  height = 500,
  xAxisTitle,
  yAxisTitle,
  dataLabelFormatter = (val) => formatSLONumber(val, 0),
  // UTC, not the viewer's browser zone - matches ApexCharts' own axis labels (which already
  // default to UTC internally), so the tooltip date always agrees with the axis it's attached to.
  tooltipXFormatter = val => new Date(val).toLocaleDateString(undefined, {timeZone: "UTC"}),
  labels,
  colors = []
}) => {
  const { t } = useTranslation();

  const chartOptions2 = {
    chart: {
      id: `line-chart-${id}`,
      type: "line",
      toolbar: {
        tools: {
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        }
      },
      zoom: {
        enabled: false
      }
    },
    labels: labels.map(t), // localized labels
    stroke: {
      curve: 'smooth'
    },
    xaxis: {
      type: "datetime",
      title: {
        text: t(xAxisTitle)
      },
      labels: {
        rotate: -45
      }
    },
    yaxis: {
      title: {
        text: t(yAxisTitle)
      },
      labels: {
        formatter: dataLabelFormatter
      }
    },
    dataLabels: {
      enabled: false
    },
    tooltip: {
      shared: true,
      intersect: false,

      x: {
        formatter: tooltipXFormatter
      },
      y: {
        formatter: (value, { series, seriesIndex, dataPointIndex, w }) => {
          // Show empty string if value is undefined due to toggle
          if (value === null || value === undefined) return "-";
          return dataLabelFormatter(value);
        }
      }
    },
    legend: {
      position: "bottom",
      fontSize: "12px"
    },
    colors: colors, // you provide per-series colors in prep
    title: {
      text: t(title),
      align: "center"
    }
  };
  const chartOptions = {
    chart: {
      id: `line-chart`,
      type: "line",
      toolbar: { tools: { zoom: false, zoomin: false, zoomout: false, pan: false, reset: false } },
      zoom: { enabled: false },
      events: {
        // Prevent the user from turning off the last visible series
        legendClick: (chartContext, seriesIndex, config) => {
          const g = config?.globals;
          if (!g) return; // let default behavior happen

          const total = g.seriesNames?.length ?? g.series?.length ?? 0;

          // current collapsed indices
          const collapsed = new Set(g.collapsedSeriesIndices || []);
          const isCurrentlyVisible = !collapsed.has(seriesIndex);

          // simulate the state *after* this click
          const nextCollapsed = new Set(collapsed);
          if (isCurrentlyVisible) nextCollapsed.add(seriesIndex);
          else nextCollapsed.delete(seriesIndex);

          const nextVisible = total - nextCollapsed.size;

          // Block if this click would hide the last visible series
          if (nextVisible === 0) {
            return false; // cancel default toggle
          }
          // otherwise, allow default toggle (return nothing)
        }
      }
    },
    noData: {
      text: t("No data!"),
      align: "center",
      verticalAlign: "middle"
    },
    labels: labels.map(t),
    stroke: { curve: "smooth" },
    xaxis: { type: "datetime", title: { text: t(xAxisTitle) }, labels: { rotate: -45 } },
    yaxis: { title: { text: t(yAxisTitle) }, labels: { formatter: dataLabelFormatter } },
    dataLabels: { enabled: false },
    tooltip: {
      shared: true,
      intersect: false,
      x: { formatter: tooltipXFormatter },
      y: {
        formatter: (value) => (value === null || value === undefined ? "-" : dataLabelFormatter(value))
      }
    },
    legend: { position: "bottom", fontSize: "12px" },
    colors,
    title: { text: t(title), align: "center" }
  };

  return (
      <>
        <>
        </>
        <Fragment rendered={!isLoading}>
          <Fragment rendered={series && series.length > 0}>
            {/* Always render the chart. Apex will show noData text when series is empty. */}
            <Chart options={chartOptions} series={series} type="line" height={height} />
          </Fragment>
          <Fragment rendered={(!series || series.length === 0)}>
          <div className="chart-empty-container has-margin-20 has-min-height-300 is-flex flex-vertical flex-justify-center flex-align-items-center">
            <p>
              <i className="fz fz-empty-set extra-large has-text-grey-lighter"></i>
            </p>
            <p>{t("No data!")}</p>
          </div>
        </Fragment>
        </Fragment>
        <Fragment rendered={!!isLoading}>
          <div className="chart-loader-container has-margin-20">
            <ShimmerLoader />
          </div>
        </Fragment>
      </>
  );

};

LineChartTimeSeriesWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  series: PropTypes.array.isRequired,
  colors: PropTypes.array.isRequired,
  labels: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  height: PropTypes.number,
  stacked: PropTypes.bool,
  xAxisTitle: PropTypes.string,
  yAxisTitle: PropTypes.string,
  dataLabelFormatter: PropTypes.func,
  tooltipXFormatter: PropTypes.func,
  tooltipYFormatter: PropTypes.func
};

export default LineChartTimeSeriesWidget;
